import React, { useEffect, useState } from 'react';
import { Clapperboard, Loader2, PlayCircle } from 'lucide-react';
import api from '../lib/api';
import { useLang, tx } from '../i18n/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { useCategories } from '../hooks/useCategories';
import { useLibraryList } from '../hooks/useLibraryList';
import { hasAccess } from '../components/LockedDialog';
import { PageHero } from '../components/articles/ArticlesHero';
import { ArticleCategoryTiles } from '../components/articles/ArticleCategoryTiles';
import { VideoCard } from '../components/library/VideoCard';
import { LibraryToolbar } from '../components/library/LibraryToolbar';
import { LibraryPagination } from '../components/library/LibraryPagination';
import { LibraryCta, ListHeading } from '../components/library/LibraryCta';
import { normalizeVideo, VIDEOS_HERO_IMG } from '../components/articles/articleUtils';

export default function Videos() {
  const { lang } = useLang();
  const L = (ar, en) => tx(lang, ar, en);
  const { user } = useAuth();
  const { categories, getLabel } = useCategories();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const list = useLibraryList(videos);
  const canAccess = hasAccess(user);

  useEffect(() => {
    api.get('/videos').then(({ data }) => setVideos((data || []).map(normalizeVideo))).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <main data-testid="videos-page" className="page-y min-h-screen">
      <div className="site-container">
        <PageHero
          testId="videos-hero" image={VIDEOS_HERO_IMG} BadgeIcon={Clapperboard}
          badge={L('مكتبة الفيديوهات', 'Video library')}
          title={L('مكتبة الفيديوهات', 'Video Library')}
          highlight={L('تعلم الأتمتة الصناعية', 'Learn industrial automation')} rest={L('خطوة بخطوة', 'step by step')}
          desc={L('مكتبة شاملة من الفيديوهات التعليمية التطبيقية في PLC و SCADA والمحاكاة والبرمجة الصناعية، منظمة حسب المجال والمستوى.', 'A complete library of hands-on tutorials on PLC, SCADA, simulation and industrial programming, organised by field and level.')} />
        <LibraryToolbar list={list} categories={categories} placeholder={L('ابحث عن فيديو...', 'Search for a video...')} testPrefix="videos" />
        <ArticleCategoryTiles categories={categories} posts={videos} active={list.cat} onChange={list.setCat} testPrefix="videos-cat" />
        <ListHeading Icon={PlayCircle} title={L('جميع الفيديوهات', 'All videos')} list={list} unit={{ ar: 'فيديو', en: 'videos' }} testId="videos-count" />
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>
        ) : list.filtered.length === 0 ? (
          <div data-testid="videos-empty" className="text-center text-slate-500 py-16 bg-white rounded-2xl border border-dashed border-slate-200">{L('لا توجد فيديوهات مطابقة', 'No matching videos')}</div>
        ) : (
          <>
            <div data-testid="videos-grid" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {list.visible.map((v, i) => (
                <VideoCard key={v.id} video={v} index={i} categories={categories} getLabel={getLabel} locked={!canAccess && !v.is_free} />
              ))}
            </div>
            <LibraryPagination list={list} testPrefix="videos" />
          </>
        )}
        <LibraryCta />
      </div>
    </main>
  );
}
